import { CopyPlus, X } from "lucide-react";
import { nanoid } from "nanoid";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import heic2any from "heic2any";
import { PlantAnalysisDisplay } from "./PlantAnalysisDisplay";

export type LocalFileType = {
  id: string;
  type: "local";
  file: File;
  addedAt?: Date;
};

export type ServerFileType = {
  id: string;
  type: "server";
  url: string;
  isDeleted?: boolean;
  addedAt?: Date;
};

export type FileStateType = LocalFileType | ServerFileType;

type DevicePhotosType = {
  photos: FileStateType[];
  setPhotos: React.Dispatch<React.SetStateAction<FileStateType[]>>;
  standalone?: boolean;
  labelStyle?: string;
  onSave?: (updatedPhotos: FileStateType[]) => Promise<void>;
  deviceId?: string;
  accountId?: string;
  autoOpenFirst?: boolean;
};

export default function DevicePhotos({
  photos,
  setPhotos,
  standalone = false,
  labelStyle,
  onSave,
  deviceId,
  accountId,
  autoOpenFirst = false,
}: DevicePhotosType) {
  const [previewImage, setPreviewImage] = useState<FileStateType | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [imageLoaded, setImageLoaded] = useState(false);
  const hasAutoOpened = useRef(false);

  // Auto-open first photo if requested
  useEffect(() => {
    if (autoOpenFirst && photos.length > 0 && !hasAutoOpened.current) {
      const visiblePhotos = photos.filter(
        (p) => p.type === "local" || (p.type === "server" && !p.isDeleted)
      );
      if (visiblePhotos.length > 0) {
        hasAutoOpened.current = true;
        setPreviewImage(visiblePhotos[0]);
      }
    }
  }, [autoOpenFirst, photos]); // Run when autoOpenFirst or photos change

  // Debug previewImage state changes
  useEffect(() => {
    console.log('🖼️ previewImage changed:', previewImage ? `${previewImage.type} - ${previewImage.id}` : 'null');
    console.log('🖼️ Window dimensions:', window.innerWidth, 'x', window.innerHeight);
    console.log('🖼️ Is mobile?', window.innerWidth < 768);
    
    // Reset image loaded state when preview image changes
    setImageLoaded(false);
    
    // Debug modal visibility after short delay
    if (previewImage) {
      setTimeout(() => {
        const modalElement = document.querySelector('[data-modal="photo-preview"]');
        console.log('🔍 Modal element found?', !!modalElement);
        if (modalElement) {
          const rect = modalElement.getBoundingClientRect();
          console.log('🔍 Modal position:', {
            x: rect.x,
            y: rect.y, 
            width: rect.width,
            height: rect.height,
            top: rect.top,
            left: rect.left,
            right: rect.right,
            bottom: rect.bottom
          });
          console.log('🔍 Modal computed style display:', window.getComputedStyle(modalElement).display);
          console.log('🔍 Modal computed style visibility:', window.getComputedStyle(modalElement).visibility);
          console.log('🔍 Modal computed style zIndex:', window.getComputedStyle(modalElement).zIndex);
          console.log('🔍 Modal computed style opacity:', window.getComputedStyle(modalElement).opacity);
          console.log('🔍 Modal computed style transform:', window.getComputedStyle(modalElement).transform);
        }
      }, 100);
    }
  }, [previewImage]);

  // Helper function to check if file is HEIC
  const isHeicFile = (file: File) => {
    return file.type === "image/heic" || 
           file.type === "image/heif" || 
           file.name.toLowerCase().endsWith('.heic');
  };

  // Helper function to format date as MM/DD/YY using UTC to avoid timezone issues
  const formatDate = (date: Date) => {
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };


  // Generate preview URL for a file
  const getPreviewUrl = (photo: FileStateType) => {
    if (photo.type === "server") return photo.url;
    
    // For local files
    if (photo.type === "local") {
      // Check if we have a cached preview URL (for converted HEIC files)
      if (previewUrls[photo.id]) {
        return previewUrls[photo.id];
      }
      
      // For non-HEIC files, create object URL directly
      if (!isHeicFile(photo.file)) {
        return URL.createObjectURL(photo.file);
      }
      
      // For HEIC files, return empty string (loading spinner will be shown)
      return "";
    }
    
    return "";
  };

  // Convert HEIC files to generate preview URLs
  useEffect(() => {
    const convertHeicFiles = async () => {
      const localPhotos = photos.filter(p => p.type === "local") as LocalFileType[];
      
      for (const photo of localPhotos) {
        if (isHeicFile(photo.file) && !previewUrls[photo.id]) {
          // Mark as loading
          setLoadingImages(prev => new Set(prev).add(photo.id));
          
          try {
            const convertedBlob = await heic2any({
              blob: photo.file,
              toType: "image/jpeg",
              quality: 0.8
            });
            
            const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
            const previewUrl = URL.createObjectURL(jpegBlob);
            
            setPreviewUrls(prev => ({
              ...prev,
              [photo.id]: previewUrl
            }));
            
            // Remove from loading
            setLoadingImages(prev => {
              const newSet = new Set(prev);
              newSet.delete(photo.id);
              return newSet;
            });
          } catch (error) {
            console.error(`Failed to convert HEIC for preview: ${photo.file.name}`, error);
            // Remove from loading even on error
            setLoadingImages(prev => {
              const newSet = new Set(prev);
              newSet.delete(photo.id);
              return newSet;
            });
          }
        }
      }
    };
    
    convertHeicFiles();
  }, [photos]);

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Monitor for photo uploads completing (local -> server transition)
  useEffect(() => {
    const serverPhotos = photos.filter(p => p.type === "server");
    const currentServerCount = serverPhotos.length;
    
    // If we have a local preview image but it's not in the photos array anymore,
    // it means it was uploaded and replaced with a server photo
    // Only auto-switch in standalone mode with deviceId (for analysis)
    if (previewImage?.type === "local" && standalone && deviceId) {
      const localStillExists = photos.find(p => p.id === previewImage.id);
      if (!localStillExists && serverPhotos.length > 0) {
        // Switch to the newest server photo (likely the one just uploaded)
        const newestServerPhoto = serverPhotos[serverPhotos.length - 1];
        console.log('🔄 Local photo disappeared, switching to server photo:', newestServerPhoto);
        setPreviewImage(newestServerPhoto);
      }
    }
  }, [photos, previewImage]);

  // Keyboard navigation for desktop
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only enable on desktop (768px and above) and when modal is open
      if (!previewImage || window.innerWidth < 768) return;

      const visiblePhotos = photos.filter(
        (p) => p.type === "local" || (p.type === "server" && !p.isDeleted)
      ).reverse(); // Same order as displayed

      const currentIndex = visiblePhotos.findIndex(p => p.id === previewImage.id);
      
      if (event.key === 'ArrowLeft' && currentIndex > 0) {
        event.preventDefault();
        setPreviewImage(visiblePhotos[currentIndex - 1]);
      } else if (event.key === 'ArrowRight' && currentIndex < visiblePhotos.length - 1) {
        event.preventDefault();
        setPreviewImage(visiblePhotos[currentIndex + 1]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setPreviewImage(null);
      }
    };

    if (previewImage) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewImage, photos]);

  const onFileInputChange = (files: FileList | null) => {
    const componentId = standalone ? 'DeviceDetailPage' : 'EditDeviceModal';
    console.log(`📸 [${componentId}] File input changed, files:`, files, 'standalone:', standalone, 'deviceId:', deviceId);
    if (files) {
      const fileArray = Array.from(files);
      console.log(`📸 [${componentId}] Processing files:`, fileArray.length);

      const newFiles = fileArray.map((fa) => ({
        id: nanoid(),
        type: "local" as const,
        file: fa,
        addedAt: new Date(),
      }));

      console.log('📸 New files created:', newFiles);

      setPhotos((prev) => {
        const newPhotosState = [...prev, ...newFiles];
        console.log('📸 Updated photos state:', newPhotosState);

        if (standalone && onSave) {
          console.log('📸 Calling onSave');
          onSave(newPhotosState);
        }

        return newPhotosState;
      });

      // Auto-open modal for the first newly added photo (only in standalone mode with deviceId for analysis)
      if (standalone && newFiles.length > 0 && deviceId) {
        console.log('🚀 Opening modal - standalone:', standalone, 'newFiles:', newFiles[0]);
        console.log('🚀 Current previewImage before setting:', previewImage);
        setTimeout(() => {
          setPreviewImage(newFiles[0]);
          console.log('🚀 Called setPreviewImage with:', newFiles[0]);
        }, 100);
      } else {
        console.log('❌ Not opening modal - standalone:', standalone, 'deviceId:', deviceId, 'newFiles length:', newFiles.length);
      }
    }
  };

  const removeFileAtId = (idToRemove: string) => {
    setPhotos((prev) => {
      const fileToRemove = prev.find((p) => p.id === idToRemove);

      if (!fileToRemove) {
        return prev;
      }

      let newPhotosState: FileStateType[];

      if (fileToRemove.type === "local") {
        // File is not uploaded yet - just remove from state
        newPhotosState = prev.filter((p) => p.id !== idToRemove);
      } else {
        newPhotosState = prev.map((p) => {
          if (p.id === idToRemove) {
            return {
              ...p,
              isDeleted: true,
            };
          }

          return p;
        });
      }

      if (standalone && onSave) {
        onSave(newPhotosState);
      }

      return newPhotosState;
    });
  };

  return (
    <>
      <div className="mb-4">
        <label className={labelStyle}>Photos</label>

        <div className="flex mt-2 gap-2">
          <label
            className="flex-shrink-0 border border-blue-700 bg-blue-100 border-dashed flex justify-center items-center h-32 w-24 flex-col p-2 text-xs gap-4 text-blue-700 opacity-50 hover:opacity-100 cursor-pointer text-center"
            htmlFor="device-photos"
          >
            <CopyPlus />
            ADD
            <br />
            PHOTOS
            <input
              id="device-photos"
              className="hidden"
              type="file"
              onChange={(e) => onFileInputChange(e.target.files)}
              accept="image/*"
              multiple
            />
          </label>

          {photos.filter(
            (p) => p.type === "local" || (p.type === "server" && !p.isDeleted)
          ).length > 0 && (
            <div className="h-32 border border-l border-slate-300"></div>
          )}

          <div className="flex overflow-x-auto">
            {photos
              .filter(
                (p) =>
                  p.type === "local" || (p.type === "server" && !p.isDeleted)
              )
              .reverse()
              .map((p, i) => {
                const isLoading = p.type === "local" && isHeicFile(p.file) && loadingImages.has(p.id);
                const previewUrl = getPreviewUrl(p);
                
                return (
                  <div
                    key={p.id}
                    className={`flex-shrink-0 h-32 w-24 border relative mr-2 ${
                      standalone ? "cursor-pointer" : ""
                    }`}
                    onClick={() => standalone && setPreviewImage(p)}
                  >
                    {isLoading || !previewUrl ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-50">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : (
                      <img
                        alt="uploaded photos"
                        src={previewUrl}
                        className="w-full h-full object-contain"
                      />
                    )}
                    <div
                    className="absolute top-0 right-0 w-4 h-4 bg-slate-200 text-red-600 cursor-pointer opacity-75 hover:opacity-100"
                    onClick={(evt) => {
                      evt.preventDefault();
                      evt.stopPropagation();
                      removeFileAtId(p.id);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </div>
                </div>
                );
              })}
          </div>
        </div>
      </div>

      {previewImage && createPortal(
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[99998]"
            onClick={() => setPreviewImage(null)}
          ></div>

          <div 
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
            data-modal="photo-preview"
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              zIndex: 99999,
              pointerEvents: 'auto'
            }}
          >
            <div 
              className="relative bg-white shadow-2xl rounded-lg overflow-hidden flex flex-col md:flex-row w-[95vw] md:w-[90vw] h-[80vh] md:h-[95vh] max-w-[95vw] md:max-w-[90vw] max-h-[80vh] md:max-h-[95vh]"
            >
              <div
                className="absolute right-2 top-2 w-8 h-8 p-1 bg-white/80 rounded-full cursor-pointer hover:bg-white z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('❌ Close button clicked');
                  setPreviewImage(null);
                }}
              >
                <X className="w-6 h-6 text-slate-900" />
              </div>

              {/* Photo Added Date */}
              {previewImage && (
                <div className="absolute left-2 top-2 bg-white/80 px-2 py-1 rounded text-xs text-gray-600 z-10">
                  Photo added: {previewImage.addedAt ? formatDate(previewImage.addedAt) : 
                    previewImage.type === "local" ? "Just now" : 
                    formatDate(new Date())}
                </div>
              )}

              {/* Image Section */}
              <div className="flex-1 flex items-center justify-center bg-gray-50 relative">
                {!imageLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4 bg-gradient-to-br from-gray-100 to-gray-200">
                    {/* Low-res placeholder pattern */}
                    <div className="w-32 h-32 bg-gray-300 rounded-lg flex items-center justify-center mb-4">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" className="text-gray-400">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="21,15 16,10 5,21" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                    <div className="text-gray-600 text-center">
                      <div className="font-medium text-lg">Loading image...</div>
                      <div className="text-sm text-gray-500 mt-1">
                        {previewImage.type === 'local' && isHeicFile(previewImage.file) 
                          ? 'Converting HEIC format...' 
                          : 'Preparing image...'}
                      </div>
                    </div>
                  </div>
                )}
                <img
                  alt="Plant photo"
                  className={`max-w-full max-h-[50vh] md:max-h-[95vh] object-contain transition-opacity duration-500 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  src={getPreviewUrl(previewImage)}
                  onLoad={() => {
                    console.log('📷 Image loaded in modal');
                    setImageLoaded(true);
                  }}
                  onError={() => {
                    console.log('❌ Image failed to load in modal');
                    setImageLoaded(true); // Hide loading state even on error
                  }}
                />
              </div>

              {/* Analysis Section */}
              {deviceId && (
                <div className="w-full md:w-80 bg-white p-4 border-t md:border-t-0 md:border-l border-gray-200 overflow-y-auto max-h-64 md:max-h-none">
                  <h3 className="font-semibold text-lg text-gray-900 mb-3 hidden md:block">Plant Analysis</h3>
                  {previewImage.type === "server" ? (
                    <PlantAnalysisDisplay 
                      photoUrl={previewImage.url}
                      deviceId={deviceId}
                      accountId={accountId}
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                      <div className="flex items-center space-x-2 text-blue-600">
                        <span className="text-4xl mr-3">🔍</span>
                        <div>
                          <div className="text-m pt-3 font-bold">
                            Analyzing Plant Health...
                          </div>
                          <div className="text-xs pb-3 text-gray-500">
                            Feel free to close this window while the image is processing 
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
                        <span>Analysis in progress...</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
